import React, { useMemo } from 'react';
import { DeviceLogEntry } from './deviceLogs';
import { NEUTRAL_CLASS, RESULT_CLASS } from './resultTone';
import {
  AttemptOutcome,
  SetupAttempt,
  SetupSummaryResult,
  WIFI_STATUS,
  WifiTry,
  formatMs,
  summarizeSetup,
} from './summarizeSetup';

interface Props {
  entries: DeviceLogEntry[];
}

const NOT_LOGGED = 'not logged';

const OUTCOME: Record<AttemptOutcome, { label: string; tone: string }> = {
  cloud_confirmed: { label: 'Cloud confirmed', tone: 'success' },
  proceeded_unconfirmed: { label: 'Proceeded without cloud confirmation', tone: 'retry' },
  wifi_joined_unconfirmed: { label: 'WiFi joined, cloud not confirmed in this session', tone: 'retry' },
  wifi_failed: { label: 'WiFi failed', tone: 'failure' },
  wifi_skipped: { label: 'WiFi step skipped', tone: 'retry' },
  stopped_after_connect: { label: 'Stopped after BLE connect', tone: 'retry' },
  found_not_connected: { label: 'Clip found, never connected', tone: 'retry' },
  no_clip_found: { label: 'No Clip found', tone: 'failure' },
  bluetooth_off: { label: 'Bluetooth off', tone: 'failure' },
  no_scan: { label: 'No scan started', tone: 'skipped' },
};

/** What the app's status word on the WiFi reply means. */
const STATUS_LABEL: Record<string, string> = {
  [WIFI_STATUS.success]: 'joined',
  [WIFI_STATUS.noConnection]: 'could not join the network',
  [WIFI_STATUS.badCredentials]: 'bad password',
  [WIFI_STATUS.serverNotFound]: 'network joined, cloud server not reachable',
  [WIFI_STATUS.timeout]: 'timed out',
  [WIFI_STATUS.writeFailed]: 'credential write failed',
};

/** The Clip firmware's WiFi status codes (wifi_status_t), shown next to the app's word so the operator sees what the Clip itself reported. */
const REPLY_CODE_LABEL: Record<string, string> = {
  '0x00': 'no error',
  '0x01': 'no access point found',
  '0x02': 'bad access point login',
  '0x03': 'no server found',
  '0x04': 'round-trip data timeout',
};

const CREDENTIAL_SOURCE_LABEL: Record<string, string> = {
  saved_prefill: 'saved password',
  retry_prefill: 'same password as the last try',
  typed: 'typed password',
  none: 'no password',
};

const HELP = {
  bluetooth: 'Phone Bluetooth adapter state when the attempt started, and the Android permission check when the app logged one.',
  scan: 'BLE scan result: whether a Clip answered, how many Bluetooth devices were in range, the signal strength, and how many scans found nothing first.',
  connect: 'Time from the connect request to the BLE connection, with pairing retries and status-read timeouts.',
  clip: 'What the Clip reported in its first status reply. A clock at the cold-boot default means the Clip had just powered on; a set clock means it was already running.',
  wifi: 'Each credential write to the Clip and what the Clip answered. The reply time comes from the app’s own measurement when it logged one, otherwise from the timestamps of the write and the reply.',
  cloud: 'Whether the app saw the cloud confirm the Clip’s activation, and how long after the attempt started.',
};

function plural(n: number, noun: string, pluralNoun = `${noun}s`): string {
  return `${n} ${n === 1 ? noun : pluralNoun}`;
}

function bluetoothText(a: SetupAttempt): string {
  const state =
    a.bluetooth === 'off_then_on' ? 'off, then turned on' : a.bluetooth ?? NOT_LOGGED;
  if (a.blePermission === 'denied') return `${state}, permission denied`;
  if (a.blePermission === 'denied_then_granted') return `${state}, permission granted after a prompt`;
  return state;
}

function scanText(a: SetupAttempt): string {
  if (a.clipFound) {
    const name = a.clipName ?? 'the Clip';
    const parts = [a.totalSeen === null ? `found ${name}` : `found ${name} among ${plural(a.totalSeen, 'BLE device')}`];
    if (a.rssi !== null) parts.push(`RSSI ${a.rssi} dBm`);
    if (a.emptyScans > 0) parts.push(`after ${plural(a.emptyScans, 'empty scan')}`);
    return parts.join(', ');
  }
  if (a.scans > 0) {
    const seen = a.totalSeen !== null ? ` (${plural(a.totalSeen, 'BLE device')} seen)` : '';
    return `${plural(a.scans, 'scan')}, no Clip found${seen}`;
  }
  return NOT_LOGGED;
}

function connectText(a: SetupAttempt): string {
  if (!a.connected) return a.clipFound ? 'not connected' : NOT_LOGGED;
  const parts = [a.connectMs === null ? 'connected, time not logged' : formatMs(a.connectMs)];
  if (a.connectAttempt !== null && a.connectAttempt > 1) {
    parts.push(`attempt ${a.connectAttempt}${a.connectMaxAttempts !== null ? ` of ${a.connectMaxAttempts}` : ''}`);
  }
  if (a.pairingRetries > 0) parts.push(plural(a.pairingRetries, 'pairing retry', 'pairing retries'));
  if (a.statusTimeouts > 0) parts.push(plural(a.statusTimeouts, 'status-read timeout'));
  return parts.join(', ');
}

function clipText(a: SetupAttempt): string {
  const parts: string[] = [];
  if (a.firmware) parts.push(`firmware ${a.firmware}`);
  if (a.mode) parts.push(a.mode);
  if (a.statusFlags) parts.push(`status ${a.statusFlags}`);
  if (a.clockAtDefault === true) {
    parts.push(
      a.secondsSinceBoot === null
        ? 'clock at the cold-boot default'
        : `clock at the cold-boot default, about ${a.secondsSinceBoot} s after power-on`,
    );
  } else if (a.clockAtDefault === false) {
    parts.push(`clock already set (${a.clipClock}), the Clip was running before this setup`);
  }
  return parts.length > 0 ? parts.join(', ') : NOT_LOGGED;
}

function tryText(t: WifiTry, index: number): string {
  const parts: string[] = [`try ${index + 1}`];
  if (t.ssid) parts.push(`"${t.ssid}"`);
  let result: string;
  if (t.status === WIFI_STATUS.timeout && !t.replied) result = 'no reply before the app gave up waiting';
  else if (t.status !== null) result = STATUS_LABEL[t.status] ?? t.status;
  else result = t.replied ? 'replied, status not logged' : 'no reply';
  if (t.replyCode && t.status !== WIFI_STATUS.success) {
    const code = REPLY_CODE_LABEL[t.replyCode];
    result += ` (Clip code ${t.replyCode}${code ? `: ${code}` : ''})`;
  }
  if (t.replyMs !== null) {
    result += ` in ${formatMs(t.replyMs)}${t.replySource === 'computed' ? ' (from timestamps)' : ''}`;
  }
  parts.push(result);
  if (t.credentialSource) parts.push(CREDENTIAL_SOURCE_LABEL[t.credentialSource] ?? t.credentialSource);
  if (t.credentialChanged === true) parts.push('password changed');
  else if (t.credentialChanged === false) parts.push('password unchanged');
  return parts.join(', ');
}

function wifiLines(a: SetupAttempt): string[] {
  if (a.wifiTries.length === 0) {
    if (a.wifiSkipped) return ['skipped'];
    return [a.connected ? 'no credentials sent' : NOT_LOGGED];
  }
  const lines = a.wifiTries.map(tryText);
  if (a.wifiSkipped) lines.push('then skipped');
  return lines;
}

function cloudText(a: SetupAttempt): string {
  if (a.cloudConfirmed) {
    return a.confirmMs === null ? 'confirmed' : `confirmed ${formatMs(a.confirmMs)} after the attempt started`;
  }
  if (a.verifyProceeded === 'skipped') return 'proceeded without confirmation';
  if (a.wifiTries.some((t) => t.status === WIFI_STATUS.success)) return 'not confirmed in this session';
  return 'not reached';
}

function outcomeText(a: SetupAttempt): string {
  const label = OUTCOME[a.outcome].label;
  if (a.outcome !== 'wifi_failed') return label;
  const last = a.wifiTries[a.wifiTries.length - 1];
  const status = last?.status;
  return status ? `${label}: ${STATUS_LABEL[status] ?? status}` : label;
}

function Row({ label, help, children }: { label: string; help: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="w-24 shrink-0 text-muted-foreground cursor-help underline decoration-dotted" title={help}>
        {label}
      </span>
      <span className="text-gray-800">{children}</span>
    </div>
  );
}

function AttemptBlock({ attempt }: { attempt: SetupAttempt }) {
  const outcome = OUTCOME[attempt.outcome];
  return (
    <div className="mt-1 pt-1 border-t border-border first:mt-0 first:pt-0 first:border-t-0">
      <div className="flex flex-wrap items-baseline gap-2 mb-0.5">
        <span className="font-semibold">Attempt {attempt.index}</span>
        <span className="font-mono">{attempt.startTs.slice(0, 19).replace('T', ' ')}</span>
        <span className="font-mono">{attempt.clipName ?? 'no Clip'}</span>
        <span className={`rounded-sm px-1.5 py-0.5 ${RESULT_CLASS[outcome.tone] ?? NEUTRAL_CLASS}`}>
          {outcomeText(attempt)}
        </span>
      </div>
      <Row label="Bluetooth" help={HELP.bluetooth}>{bluetoothText(attempt)}</Row>
      <Row label="Scan" help={HELP.scan}>{scanText(attempt)}</Row>
      <Row label="BLE connect" help={HELP.connect}>{connectText(attempt)}</Row>
      <Row label="Clip" help={HELP.clip}>{clipText(attempt)}</Row>
      <Row label="WiFi" help={HELP.wifi}>
        {wifiLines(attempt).map((line) => (
          <div key={line}>{line}</div>
        ))}
      </Row>
      <Row label="Cloud" help={HELP.cloud}>{cloudText(attempt)}</Row>
    </div>
  );
}

function beforeText(s: SetupSummaryResult): string | null {
  const parts: string[] = [];
  if (s.credentialsEnteredBefore > 0) parts.push(`credentials entered ${plural(s.credentialsEnteredBefore, 'time')}`);
  if (s.wifiSkippedBefore > 0) parts.push(`skipped ${plural(s.wifiSkippedBefore, 'time')}`);
  if (parts.length === 0) return null;
  const tail = s.attempts.length === 0 ? '; Clip setup never started' : '';
  return `WiFi screen before setup: ${parts.join(', ')}${tail}`;
}

/** Facts computed in the browser from the loaded session (FS-1186). Renders nothing for a session with no setup activity. */
const SetupSummary: React.FC<Props> = ({ entries }) => {
  const summary = useMemo(() => summarizeSetup(entries), [entries]);
  if (!summary.hasActivity) return null;
  const before = beforeText(summary);
  return (
    <section
      aria-label="Setup summary"
      className="mb-2 px-2 py-1.5 bg-gray-50 border border-border rounded-sm text-xs leading-5"
    >
      <div
        className="font-semibold mb-1 cursor-help underline decoration-dotted w-fit"
        title="Computed in the browser from this session’s entries. Milestones are read from the app’s named log events, or from the message text on older app builds. A fact the app did not log says so."
      >
        Setup summary
      </div>
      {before && <div className="text-gray-800">{before}</div>}
      {summary.attempts.map((attempt) => (
        <AttemptBlock key={attempt.index} attempt={attempt} />
      ))}
    </section>
  );
};

export default SetupSummary;
